"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useMemo } from "react";

import { useTranslations } from "next-intl";

import { Amount, AmountVariant } from "@/components/ui/Common/Amount";
import { CustomSlider } from "@/components/ui/Common/CustomSlider";
import LocationTitle from "@/components/ui/Common/LocationTitle";
import { CompletedDate } from "@/components/ui/CompletedDate";
import ResponsiveImage from "@/components/ui/ResponsiveImage";
import { CurrencyChanger } from "@/components/widgets/CurrencyChanger";
import { PropertyMainInfo } from "@/components/widgets/Property/MainInfo";
import { IImagesProperty } from "@/types/images.types";
import { ConvertedPrices } from "@/types/pricing.types";
import { cn } from "@/utils/cn";
import { isDefaultLocale } from "@/utils/locale";

import iconEmpty from "public/assets/img/empty.png";

interface Layout {
  _id: string;
  coverImage?:
    | string
    | { original?: string; mobile?: string; tablet?: string; desktop?: string };
  rooms?: number;
  area?: number;
  bedrooms?: number;
  floors?: string;
  convertedPrices?: ConvertedPrices;
  convertedPricesPerSqM?: ConvertedPrices;
  completionQuarter?: number;
  completionYear?: number;
  additionalImagesStr?: string[];
  coverImageStr?: string;
  images: IImagesProperty[];
}

interface PlanCardProps {
  layout: Layout;
  locale: string;
  slug: string;
  completedDate?: { quartal: number; year: number };
  className?: string;
  location?: Record<string, string>;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  layout,
  locale,
  slug,
  location,
  completedDate,
  className,
}) => {
  const t = useTranslations();

  const hasValidImages = useMemo(
    () => layout.images && layout.images.length > 0,
    [layout],
  );

  // Helper function to create locale-aware URLs
  const createPlanUrl = (): string => {
    if (isDefaultLocale(locale)) {
      return `/developers/${slug}/plans/${layout._id}/`;
    }
    return `/${locale}/developers/${slug}/plans/${layout._id}/`;
  };

  console.log("LAYOUT:", layout);

  return (
    <li
      key={layout._id}
      className={cn(
        "block bg-white rounded-lg shadow shadow-gray-110/25",
        "border border-gray-200 hover:border-primary",
        "transition-colors duration-200",
        className,
      )}
    >
      <CustomSlider
        className="!h-auto"
        main={
          <div className="flex">
            {hasValidImages ? (
              layout.images.map((image, index) => (
                <Link
                  href={createPlanUrl()}
                  key={index}
                  className={"flex-[0_0_100%] relative w-full h-full"}
                >
                  <ResponsiveImage
                    maxSize="tablet"
                    sizes="(max-width: 768px) 640px"
                    style={{
                      width: "100%",
                      height: "27.5rem",
                      objectFit: "cover",
                    }}
                    images={image.populated ?? []}
                    alt={"appartment"}
                  />
                </Link>
              ))
            ) : (
              <Link
                href={createPlanUrl()}
                className={" flex-[0_0_100%] relative w-full h-full"}
              >
                <Image
                  width={0}
                  height={0}
                  sizes="(max-width: 768px) 640px"
                  style={{
                    width: "100%",
                    height: "27.5rem",
                    objectFit: "cover",
                  }}
                  src={iconEmpty}
                  alt={"is empty"}
                  className="cursor-pointer"
                />
              </Link>
            )}
          </div>
        }
        length={hasValidImages ? layout.images.length : 0}
        isModal={true}
      />

      <Link href={createPlanUrl()}>
        <div className="flex flex-col gap-8 p-8">
          <h3 className="font-semibold text-lg">{t("Plan")}</h3>

          <PropertyMainInfo
            rooms={layout.rooms}
            area={layout.area}
            floors={layout.floors}
            bedrooms={layout.bedrooms}
          />

          <Amount
            className={cn("font-bold text-lg md:text-[2.1rem]")}
            convertedPrices={layout.convertedPrices}
            variant={AmountVariant.FULL}
          />

          <div className={"flex justify-between items-center"}>
            <Amount
              className={cn("font-bold text-lg md:text-[2.1rem]")}
              convertedPrices={layout.convertedPricesPerSqM}
              variant={AmountVariant.PER_SQUARE}
            />

            <CurrencyChanger
              classNames={{
                btn: "ms-auto",
                arrow: "text-red",
              }}
            />
          </div>

          <LocationTitle
            country={location?.country}
            region={location?.region}
            city={location?.city}
            district={location?.district}
            microdistrict={location?.microdistrict}
            street={location?.street}
            number={location?.number}
          />

          {(completedDate?.year || completedDate?.quartal) && (
            <div>
              <CompletedDate
                className={"text-md"}
                quartal={layout.completionQuarter || completedDate?.quartal}
                year={layout.completionYear || completedDate?.year}
              />
            </div>
          )}
        </div>
      </Link>
    </li>
  );
};
